import { useMemo } from 'react';
import {
  POLICY_SYSTEM_UNAVAILABLE_CODE,
  policySystemMessages,
  type PolicySystemState,
} from '../content/policySystemMessages';

export type ApiErrorLike = Error & {
  status?: number;
  apiBody?: {
    code?: string;
    message?: string;
    correlationId?: string;
    faultReference?: string;
    retryable?: boolean;
  };
};

export interface ResolvedApiError {
  title: string;
  body: string;
  retryable: boolean;
  faultReference?: string;
  correlationId?: string;
  supportPhone: string;
  supportPhoneHref: string;
  isPolicySystemUnavailable: boolean;
}

/**
 * Map API errors to consistent customer-facing copy (server message preferred when safe).
 */
export function useApiError(
  err: unknown,
  policySystem?: PolicySystemState | null,
  context?: 'default' | 'linkPolicy' | 'claims'
): ResolvedApiError | null {
  return useMemo(() => {
    if (err == null && policySystem?.status !== 'unavailable') {
      return null;
    }

    const e = err as ApiErrorLike | undefined;
    const code = e?.apiBody?.code;
    const correlationId = e?.apiBody?.correlationId;
    const faultReference = e?.apiBody?.faultReference;
    const serverMessage = e?.apiBody?.message?.trim();
    const isOutage =
      code === POLICY_SYSTEM_UNAVAILABLE_CODE ||
      policySystem?.status === 'unavailable';

    if (isOutage) {
      let body: string = policySystemMessages.pageBody;
      if (context === 'linkPolicy') {
        body = policySystemMessages.linkPolicy;
      } else if (context === 'claims') {
        body = policySystemMessages.claims;
      } else if (serverMessage && code === POLICY_SYSTEM_UNAVAILABLE_CODE) {
        body = serverMessage;
      }

      return {
        title: policySystemMessages.pageTitle,
        body,
        retryable: e?.apiBody?.retryable ?? true,
        correlationId: correlationId ?? policySystem?.correlationId,
        supportPhone: policySystemMessages.supportPhone,
        supportPhoneHref: policySystemMessages.supportPhoneHref,
        isPolicySystemUnavailable: true,
      };
    }

    if (!e) {
      return null;
    }

    return {
      title: 'Something went wrong',
      body: serverMessage || e.message || policySystemMessages.writeBlocked,
      retryable: e.apiBody?.retryable ?? false,
      faultReference,
      correlationId,
      supportPhone: policySystemMessages.supportPhone,
      supportPhoneHref: policySystemMessages.supportPhoneHref,
      isPolicySystemUnavailable: false,
    };
  }, [err, policySystem, context]);
}

/** Non-hook classifier for event handlers and catch blocks. */
export function isPolicySystemUnavailableApiError(err: unknown): boolean {
  const e = err as ApiErrorLike | undefined;
  return e?.status === 503 && e?.apiBody?.code === POLICY_SYSTEM_UNAVAILABLE_CODE;
}
